import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Shirt,
    Inbox,
    ShoppingCart,
    Package,
    ArrowLeft,
    Building2,
    Clock,
    Truck,
    Droplet,
    Scissors,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SolicitudesDelDiaWidget from '@/components/lavanderia/SolicitudesDelDiaWidget';
import ConfirmarRecolecciones from './components/ConfirmarRecolecciones';
import optyLavLogo from '@/images/opty_lav.png';
const LavanderiaDashboard = () => {
    const navigate = useNavigate();

    const functionalities = [
        {
            id: 'recepcion',
            title: 'Gestión de Lenceria Horizontal',
            description: 'Registrar entrega y alistamiento de ropa',
            icon: Inbox,
            path: '/lavanderia/recepcion',
            color: 'bg-blue-800',
            hoverColor: 'hover:bg-blue-900',
            iconBg: 'bg-blue-800/10',
            iconColor: 'text-blue-800',
            isReady: true,
        },
        {
            id: 'inventario',
            title: 'Inventario de lencería',
            description: 'Gestionar inventario de lencería',
            icon: Package,
            path: '/lavanderia/inventario',
            color: 'bg-blue-800',
            hoverColor: 'hover:bg-blue-900',
            iconBg: 'bg-blue-800/10',
            iconColor: 'text-blue-800',
            isReady: true,
        },
        {
            id: 'cortes-inventario',
            title: 'Cortes de inventario',
            description: 'Cerrar períodos, conteo físico y análisis por corte',
            icon: Scissors,
            path: '/lavanderia/cortes-inventario',
            color: 'bg-indigo-800',
            hoverColor: 'hover:bg-indigo-900',
            iconBg: 'bg-indigo-800/10',
            iconColor: 'text-indigo-800',
            isReady: true,
        },
        {
            id: 'inventario-vertical',
            title: 'Gestionar Lenceria vertical',
            description: 'Gestionar inventario de lencería por edificio',
            icon: Building2,
            path: '/lavanderia/inventario-vertical',
            color: 'bg-purple-800',
            hoverColor: 'hover:bg-purple-900',
            iconBg: 'bg-purple-800/10',
            iconColor: 'text-purple-800',
            isReady: true,
        },
        {
            id: 'entrega-insumos',
            title: 'Gestión de Insumos',
            description: 'Gestionar entrega y alistamiento de insumos',
            icon: ShoppingCart,
            path: '/lavanderia/entrega-insumos',
            color: 'bg-green-800',
            hoverColor: 'hover:bg-green-900',
            iconBg: 'bg-green-800/10',
            iconColor: 'text-green-800',
            isReady: true,
        },
        {
            id: 'inventario-insumos',
            title: 'Inventario de insumos',
            description: 'Gestionar inventario global de insumos',
            icon: Package,
            path: '/lavanderia/inventario-insumos',
            color: 'bg-green-800',
            hoverColor: 'hover:bg-green-900',
            iconBg: 'bg-green-800/10',
            iconColor: 'text-green-800',
            isReady: true,
        },
        {
            id: 'ropa-desmanche',
            title: 'Ropa en desmanche',
            description: 'Registrar y gestionar procesos de desmanche',
            icon: Droplet,
            path: '/lavanderia/ropa-desmanche',
            color: 'bg-orange-800',
            hoverColor: 'hover:bg-orange-900',
            iconBg: 'bg-orange-800/10',
            iconColor: 'text-orange-800',
            isReady: true,
        },
/*         {
            id: 'proflux-recepcion',
            title: 'Recepción Ropa Sucia',
            description: 'Ver recepciones del centro de lavado HOUSY HOST',
            icon: Truck,
            path: '/lavanderia/proflux-recepcion',
            color: 'bg-violet-800',
            hoverColor: 'hover:bg-violet-900',
            iconBg: 'bg-violet-800/10',
            iconColor: 'text-violet-800',
            isReady: true,
        },
        {
            id: 'proflux-horas',
            title: 'Horas Trabajadas',
            description: 'Control de tiempo del personal en HOUSY HOST',
            icon: Clock,
            path: '/lavanderia/proflux-horas',
            color: 'bg-blue-600',
            hoverColor: 'hover:bg-blue-600/50',
            iconBg: 'bg-blue-600/10',
            iconColor: 'text-blue-600',
            isReady: true,
        }, */

    ];

    const handleNavigate = (functionality) => {
        if (functionality.isReady) {
            navigate(functionality.path);
        } else {
            // Mostrar toast de "en desarrollo"
            console.log(`${functionality.title} - En desarrollo`);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 100
            }
        }
    };

    return (
        <div className="container mx-auto p-2 space-y-8">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
            >
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigate('/dashboard')}
                        className="h-10 w-10 shrink-0"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex items-center gap-4 flex-1">
                        <div className="bg-blue-50 p-3 rounded-lg shrink-0">
                            <Shirt className="h-8 w-8 text-blue-700" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Dashboard de Lavandería</h1>
                            <p className="text-gray-600 mt-1">
                                Gestión integral de operaciones de lavandería
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>


            {/* Sección: Solicitudes del Día */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
            >
                <SolicitudesDelDiaWidget />
            </motion.div>

            {/* Sección: Confirmar Recolecciones */}
{/*             <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
            >
                <ConfirmarRecolecciones />
            </motion.div> */}

            {/* Sección: Funcionalidades Principales */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
            >
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-lg">
                        <Shirt className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">Operaciones Principales</h2>
                </div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                >
                    {functionalities.filter(f => ['recepcion', 'entrega-insumos', 'inventario-vertical', 'ropa-desmanche'].includes(f.id)).map((functionality) => {
                        const Icon = functionality.icon;
                        return (
                            <motion.div key={functionality.id} variants={itemVariants}>
                                <Card
                                    className={`h-full cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${functionality.isReady
                                        ? 'hover:scale-[1.02] hover:border-blue-400'
                                        : 'opacity-75 cursor-not-allowed'
                                        }`}
                                    onClick={() => handleNavigate(functionality)}
                                >
                                    <CardHeader className="pb-4 text-center">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className={`${functionality.iconBg} p-3 rounded-lg`}>
                                                <Icon className={`h-7 w-7 ${functionality.iconColor}`} />
                                            </div>
                                            {!functionality.isReady && (
                                                <Badge variant="secondary" className="text-xs">
                                                    Próximamente
                                                </Badge>
                                            )}
                                        </div>
                                        <CardTitle className="text-lg font-semibold text-gray-900 leading-tight text-center">
                                            {functionality.title}
                                        </CardTitle>
                                        <CardDescription className="text-sm text-gray-600 mt-2 min-h-[40px] text-center">
                                            {functionality.description}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                        <Button
                                            className={`w-full ${functionality.color} ${functionality.hoverColor} text-white font-medium`}
                                            disabled={!functionality.isReady}
                                        >
                                            {functionality.isReady ? 'Acceder al módulo' : 'En Desarrollo'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </motion.div>

            {/* Sección: Inventarios */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
            >
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-amber-100 p-2 rounded-lg">
                        <Package className="h-5 w-5 text-amber-700" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">Gestión de Inventarios</h2>
                </div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                    {functionalities.filter(f => ['inventario', 'inventario-insumos'].includes(f.id)).map((functionality) => {
                        const Icon = functionality.icon;
                        return (
                            <motion.div key={functionality.id} variants={itemVariants}>
                                <Card
                                    className={`h-full cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${functionality.isReady
                                        ? 'hover:scale-[1.02] hover:border-amber-300'
                                        : 'opacity-75 cursor-not-allowed'
                                        }`}
                                    onClick={() => handleNavigate(functionality)}
                                >
                                    <CardHeader className="pb-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className={`${functionality.iconBg} p-3 rounded-lg`}>
                                                <Icon className={`h-7 w-7 ${functionality.iconColor}`} />
                                            </div>
                                            {!functionality.isReady && (
                                                <Badge variant="secondary" className="text-xs">
                                                    Próximamente
                                                </Badge>
                                            )}
                                        </div>
                                        <CardTitle className="text-lg font-semibold text-gray-900 leading-tight">
                                            {functionality.title}
                                        </CardTitle>
                                        <CardDescription className="text-sm text-gray-600 mt-2 min-h-[40px]">
                                            {functionality.description}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                        <Button
                                            className={`w-full ${functionality.color} ${functionality.hoverColor} text-white font-medium`}
                                            disabled={!functionality.isReady}
                                        >
                                            {functionality.isReady ? 'Acceder al módulo' : 'En Desarrollo'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </motion.div>

            {/* Sección: Módulos PROFLUX */}
{/*             <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
            >
                <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                    <img src={optyLavLogo} alt="Logo OptyLav" className="h-16 w-auto drop-shadow" />
                    <div className="space-y-1">
                        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">OptyLav</p>
                        <p className="text-sm text-gray-600 max-w-2xl">
                            Interoperabilidad de módulos de lavandería entre HOUSY y el centro de lavado OPTYLAV.
                        </p>
                    </div>
                </div> 
       <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                    {functionalities.filter(f => ['proflux-recepcion', 'proflux-horas'].includes(f.id)).map((functionality) => {
                        const Icon = functionality.icon;
                        return (
                            <motion.div key={functionality.id} variants={itemVariants}>
                                <Card
                                    className={`h-full cursor-pointer transition-all duration-300 hover:shadow-lg border-2 ${functionality.isReady
                                        ? 'hover:scale-[1.02] hover:border-violet-600'
                                        : 'opacity-75 cursor-not-allowed'
                                        }`}
                                    onClick={() => handleNavigate(functionality)}
                                >
                                    <CardHeader className="pb-4 flex flex-col items-center text-center">
                                        <div className="flex flex-col items-center gap-2 mb-3">
                                            <div className={`${functionality.iconBg} p-3 rounded-lg`}
                                                aria-hidden="true">
                                                <Icon className={`h-7 w-7 ${functionality.iconColor}`} />
                                            </div>
                                            {!functionality.isReady && (
                                                <Badge variant="secondary" className="text-xs">
                                                    Próximamente
                                                </Badge>
                                            )}
                                        </div>
                                        <CardTitle className="text-lg font-semibold text-gray-900 leading-tight">
                                            {functionality.title}
                                        </CardTitle>
                                        <CardDescription className="text-sm text-gray-600 mt-2 min-h-[40px]">
                                            {functionality.description}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                        <Button
                                            className={`w-full ${functionality.color} ${functionality.hoverColor} text-white font-medium`}
                                            disabled={!functionality.isReady}
                                        >
                                            {functionality.isReady ? 'Acceder al módulo' : 'En Desarrollo'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </motion.div> 
            </motion.div>*/}

        </div>
    );
};

export default LavanderiaDashboard;
